import React from 'react';
import {
  Users,
  CheckCircle,
  ArrowRight,
  AlertCircle,
  Phone,
  Mail,
  MessageSquare,
  Calendar,
  FileText,
  Tag,
  Edit,
  Trash2,
  TrendingUp
} from 'lucide-react';
import ModernDocHeader from '../../components/documentation/ModernDocHeader';

const LeadManagementDocs: React.FC = () => {
  return (
    <div>
      <ModernDocHeader
        title="Lead Management"
        description="Master the art of capturing, tracking, and converting leads in your CRM. Learn how to manage your entire sales pipeline from first contact to closed deal."
        icon={<Users className="w-6 h-6" />}
        badge="Core Feature"
        badgeColor="purple"
      />

      <div className="p-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-4xl">
          <div className="prose prose-blue dark:prose-invert max-w-none">

      {/* Overview */}
      <div className="not-prose bg-green-50 dark:bg-blue-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-brandColor dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-2">What You'll Learn</h3>
            <ul className="space-y-1 text-sm text-green-800 dark:text-green-300">
              <li>✓ How leads flow into your CRM</li>
              <li>✓ Viewing and accessing lead information</li>
              <li>✓ Managing lead activities and remarks</li>
              <li>✓ Working with custom fields</li>
              <li>✓ Lead assignment and ownership</li>
              <li>✓ Import/Export capabilities</li>
              <li>✓ Search, filter, and organize leads</li>
              <li>✓ Best practices for lead conversion</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>What is Lead Management?</h2>
      <p>
        Lead management is the process of capturing, tracking, and nurturing potential customers (leads)
        through your sales pipeline. In your CRM, every interaction with a prospect is automatically logged,
        giving you a complete 360° view of the customer journey.
      </p>

      <h2>How Leads Enter Your CRM</h2>
      <p>Your CRM captures leads automatically from multiple channels:</p>

      <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
        <div className="p-4 border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-blue-900/20 rounded-lg">
          <div className="flex items-center space-x-3 mb-2">
            <Phone className="w-5 h-5 text-brandColor" />
            <h4 className="font-semibold text-green-900 dark:text-green-200">Phone Calls</h4>
          </div>
          <p className="text-sm text-brandColor dark:text-green-300">
            Knowlarity, Mcube, Ozonetel, TCPN - automatic lead creation from incoming calls
          </p>
        </div>

        <div className="p-4 border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <div className="flex items-center space-x-3 mb-2">
            <MessageSquare className="w-5 h-5 text-green-600" />
            <h4 className="font-semibold text-green-900 dark:text-green-200">WhatsApp</h4>
          </div>
          <p className="text-sm text-green-700 dark:text-green-300">
            WATI, AiSensy - capture leads from WhatsApp Business conversations
          </p>
        </div>

        <div className="p-4 border-2 border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
          <div className="flex items-center space-x-3 mb-2">
            <svg className="w-5 h-5 text-purple-600" viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            <h4 className="font-semibold text-purple-900 dark:text-purple-200">Facebook Lead Ads</h4>
          </div>
          <p className="text-sm text-purple-700 dark:text-purple-300">
            Automatic lead sync from Facebook Pages and Lead Ad campaigns
          </p>
        </div>

        <div className="p-4 border-2 border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
          <div className="flex items-center space-x-3 mb-2">
            <svg className="w-5 h-5 text-orange-600" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
            </svg>
            <h4 className="font-semibold text-orange-900 dark:text-orange-200">Website Forms</h4>
          </div>
          <p className="text-sm text-orange-700 dark:text-orange-300">
            API integration for contact forms, landing pages, and web submissions
          </p>
        </div>
      </div>

      <div className="not-prose bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 my-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          <strong>💡 Pro Tip:</strong>
        </p>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          You can also manually add leads or import them in bulk via CSV/Excel. All leads, regardless of source,
          are managed the same way in your CRM.
        </p>
      </div>

      <h2>Accessing Your Leads</h2>

      <h3>Navigate to Leads Page</h3>
      <ol>
        <li>Click <strong>Leads</strong> in the main sidebar</li>
        <li>You'll see a table view of all your leads</li>
        <li>Use tabs to switch between different views (All Leads, My Leads, Unassigned, etc.)</li>
      </ol>

      <h3>Lead Table Columns</h3>
      <p>The leads table shows key information at a glance:</p>
      <ul>
        <li><strong>Name</strong> - Lead's full name</li>
        <li><strong>Phone/Email</strong> - Contact information</li>
        <li><strong>Source</strong> - How the lead was captured (Phone, WhatsApp, Facebook, etc.)</li>
        <li><strong>Status</strong> - Current pipeline stage (New, Contacted, Qualified, etc.)</li>
        <li><strong>Assigned To</strong> - Team member responsible for this lead</li>
        <li><strong>Created Date</strong> - When the lead entered the system</li>
        <li><strong>Last Activity</strong> - Most recent interaction</li>
        <li><strong>Custom Fields</strong> - Any additional fields you've configured</li>
      </ul>

      <div className="not-prose bg-green-50 dark:bg-blue-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 my-4">
        <p className="text-sm text-green-800 dark:text-green-300 mb-2">
          <strong>📌 Customizable Columns:</strong>
        </p>
        <p className="text-sm text-brandColor dark:text-green-300">
          You can customize which columns appear in the table by going to{' '}
          <code className="bg-green-100 dark:bg-blue-900 px-2 py-0.5 rounded">Settings → Columns</code>.
          Show/hide columns, reorder them, or add custom field columns.
        </p>
      </div>

      <h2>Viewing Lead Details</h2>
      <p>Click on any lead row to open the <strong>Lead Detail Modal</strong>. This modal contains comprehensive information organized in tabs:</p>

      <h3>Details Tab</h3>
      <ul>
        <li><strong>Basic Information</strong>: Name, phone, email, company</li>
        <li><strong>Source Details</strong>: How and when they were captured</li>
        <li><strong>Assignment</strong>: Who owns this lead</li>
        <li><strong>Pipeline Status</strong>: Current stage and progress</li>
        <li><strong>Custom Fields</strong>: All custom data points</li>
        <li><strong>Tags</strong>: Categorization labels</li>
      </ul>

      <h3>Activities Tab</h3>
      <p>Complete timeline of all interactions:</p>
      <ul>
        <li>Phone calls (with recordings)</li>
        <li>WhatsApp messages</li>
        <li>Emails sent/received</li>
        <li>Status changes</li>
        <li>Assignment changes</li>
        <li>Field updates</li>
        <li>System events</li>
      </ul>

      <h3>Remarks Tab</h3>
      <p>Notes and comments about the lead:</p>
      <ul>
        <li>Add internal notes</li>
        <li>Log conversation summaries</li>
        <li>Track important decisions</li>
        <li>Mention team members with @username</li>
        <li>Attach files and documents</li>
      </ul>

      <h3>Events Tab</h3>
      <p>Scheduled activities and follow-ups:</p>
      <ul>
        <li>Meetings and calls</li>
        <li>Follow-up reminders</li>
        <li>Deadlines and milestones</li>
        <li>Calendar integration</li>
      </ul>

      <h2>Managing Lead Information</h2>

      <h3>Editing Lead Details</h3>
      <ol>
        <li>Open the lead detail modal</li>
        <li>Click the <strong>Edit</strong> button (pencil icon)</li>
        <li>Update any fields:
          <ul>
            <li>Name, phone, email</li>
            <li>Company and position</li>
            <li>Custom fields</li>
            <li>Tags</li>
          </ul>
        </li>
        <li>Click <strong>Save</strong> to apply changes</li>
      </ol>

      <div className="not-prose bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 my-4">
        <div className="flex items-start space-x-2">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-yellow-800 dark:text-yellow-300">
              <strong>Note:</strong> All changes are automatically logged in the activity timeline.
              You can see who made changes and when.
            </p>
          </div>
        </div>
      </div>

      <h3>Changing Lead Status</h3>
      <p>Move leads through your pipeline stages:</p>
      <ol>
        <li>Open lead details</li>
        <li>Click the <strong>Status</strong> dropdown</li>
        <li>Select new stage (New → Contacted → Qualified → Proposal → Won/Lost)</li>
        <li>Optionally add a remark explaining the status change</li>
      </ol>

      <p>Or use the <strong>Pipeline View</strong>:</p>
      <ol>
        <li>Navigate to <code>Pipeline</code> page</li>
        <li>Drag and drop lead cards between columns</li>
        <li>Visual Kanban-style workflow</li>
      </ol>

      <h3>Assigning Leads</h3>
      <p>Transfer lead ownership to team members:</p>
      <ol>
        <li>Select one or multiple leads (checkbox)</li>
        <li>Click <strong>Assign</strong> button in the toolbar</li>
        <li>Choose a team member from dropdown</li>
        <li>Optionally notify them via email</li>
        <li>Click <strong>Confirm</strong></li>
      </ol>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 my-6">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-base font-semibold text-green-900 dark:text-green-200 mb-2">
              Auto-Assignment with Round Robin
            </h4>
            <p className="text-sm text-green-800 dark:text-green-300">
              Set up automatic lead distribution rules in <code>Settings → Round Robin</code>.
              New leads can be automatically assigned to team members based on source, time, or custom criteria.
            </p>
            <a
              href="/docs/round-robin"
              className="inline-flex items-center text-sm text-green-700 dark:text-green-300 hover:underline mt-2"
            >
              Learn about Round Robin <ArrowRight className="w-4 h-4 ml-1" />
            </a>
          </div>
        </div>
      </div>

      <h2>Adding Remarks and Notes</h2>
      <p>Document conversations and important information:</p>

      <h3>How to Add a Remark</h3>
      <ol>
        <li>Open lead details → <strong>Remarks</strong> tab</li>
        <li>Type your note in the text area</li>
        <li>Options:
          <ul>
            <li><strong>Rich Text</strong>: Bold, italic, lists, links</li>
            <li><strong>Mentions</strong>: Tag team members with @username</li>
            <li><strong>Files</strong>: Attach documents, images, screenshots</li>
            <li><strong>Private</strong>: Mark as private (visible only to you)</li>
          </ul>
        </li>
        <li>Click <strong>Add Remark</strong></li>
      </ol>

      <h3>Remark Best Practices</h3>
      <div className="not-prose space-y-2 my-4">
        <div className="flex items-start space-x-2 text-sm">
          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
          <span className="text-gray-700 dark:text-gray-300">
            <strong>Be Specific:</strong> "Interested in Enterprise plan, needs 50 user licenses" vs "Good call"
          </span>
        </div>
        <div className="flex items-start space-x-2 text-sm">
          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
          <span className="text-gray-700 dark:text-gray-300">
            <strong>Include Next Steps:</strong> "Follow up on Friday after budget approval"
          </span>
        </div>
        <div className="flex items-start space-x-2 text-sm">
          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
          <span className="text-gray-700 dark:text-gray-300">
            <strong>Tag Team Members:</strong> "@john - please send pricing deck by EOD"
          </span>
        </div>
        <div className="flex items-start space-x-2 text-sm">
          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
          <span className="text-gray-700 dark:text-gray-300">
            <strong>Log Objections:</strong> "Price concern - competitor quoted 20% lower"
          </span>
        </div>
      </div>

      <h2>Working with Custom Fields</h2>
      <p>
        Custom fields allow you to capture additional information specific to your business.
        Examples: Budget, Company Size, Industry, Product Interest, etc.
      </p>

      <h3>Viewing Custom Fields</h3>
      <p>Custom fields appear in:</p>
      <ul>
        <li>Lead detail modal → Details tab</li>
        <li>Lead edit form</li>
        <li>Lead table (if column enabled)</li>
        <li>Export files</li>
      </ul>

      <h3>Configuring Custom Fields</h3>
      <ol>
        <li>Navigate to <code>Settings → Form Fields</code></li>
        <li>Click <strong>Add Custom Field</strong></li>
        <li>Configure:
          <ul>
            <li>Field Name (e.g., "Budget Range")</li>
            <li>Field Type (Text, Number, Dropdown, Date, etc.)</li>
            <li>Required or Optional</li>
            <li>Default Value</li>
            <li>Options (for dropdown/radio)</li>
          </ul>
        </li>
        <li>Click <strong>Save</strong></li>
      </ol>

      <div className="not-prose bg-green-50 dark:bg-blue-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 my-4">
        <p className="text-sm text-green-800 dark:text-green-300">
          <strong>💡 Common Custom Fields:</strong> Budget, Company Size, Industry, Decision Maker,
          Product Interest, Lead Score, Referral Source, Pain Points, Timeline
        </p>
      </div>

      <h2>Duplicate Lead Handling</h2>
      <p>
        Your CRM automatically detects duplicate leads based on phone number and email.
        When a duplicate is detected:
      </p>

      <h3>Automatic Duplicate Detection</h3>
      <ul>
        <li><strong>Primary Lead</strong>: First occurrence becomes the main record</li>
        <li><strong>Duplicate Indicator</strong>: Badge showing number of duplicates</li>
        <li><strong>Merged Activities</strong>: All interactions logged under primary lead</li>
        <li><strong>Multiple Sources</strong>: Track if lead came from different channels</li>
      </ul>

      <h3>Viewing Duplicates</h3>
      <ol>
        <li>Look for duplicate badge on lead row</li>
        <li>Click badge to see all duplicate records</li>
        <li>Review each duplicate's data</li>
        <li>Choose to merge or keep separate</li>
      </ol>

      <h3>Merging Duplicates</h3>
      <ol>
        <li>Open primary lead details</li>
        <li>Click <strong>View Duplicates</strong></li>
        <li>Select duplicates to merge</li>
        <li>Choose which data to keep from each record</li>
        <li>Confirm merge</li>
        <li>All activities and remarks are combined</li>
      </ol>

      <h2>Import and Export</h2>

      <h3>Importing Leads (Bulk Upload)</h3>
      <ol>
        <li>Navigate to <strong>Leads</strong> page</li>
        <li>Click <strong>Import</strong> button</li>
        <li>Download the CSV template</li>
        <li>Fill in your lead data:
          <ul>
            <li>Required: Name, Phone or Email</li>
            <li>Optional: Company, Status, Custom Fields</li>
            <li>One lead per row</li>
          </ul>
        </li>
        <li>Upload your completed CSV file</li>
        <li>Map CSV columns to CRM fields</li>
        <li>Review preview</li>
        <li>Click <strong>Import</strong></li>
      </ol>

      <div className="not-prose bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6 my-4">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-base font-semibold text-yellow-900 dark:text-yellow-200 mb-2">
              Import Tips
            </h4>
            <ul className="space-y-1 text-sm text-yellow-800 dark:text-yellow-300">
              <li>• Phone numbers should be in format: 9876543210 (10 digits, no country code)</li>
              <li>• Duplicates are automatically detected and skipped</li>
              <li>• Maximum 10,000 leads per import</li>
              <li>• Use UTF-8 encoding for special characters</li>
              <li>• Date format: YYYY-MM-DD (e.g., 2024-01-15)</li>
            </ul>
          </div>
        </div>
      </div>

      <h3>Exporting Leads</h3>
      <ol>
        <li>Navigate to <strong>Leads</strong> page</li>
        <li>Apply filters if you want to export a subset</li>
        <li>Click <strong>Export</strong> button</li>
        <li>Choose format:
          <ul>
            <li><strong>CSV</strong> - For Excel, Google Sheets</li>
            <li><strong>Excel (XLSX)</strong> - Native Excel format</li>
          </ul>
        </li>
        <li>Select columns to include</li>
        <li>Click <strong>Download</strong></li>
      </ol>

      <p>Export includes:</p>
      <ul>
        <li>All lead fields (basic + custom)</li>
        <li>Current status and stage</li>
        <li>Assignment information</li>
        <li>Source and creation date</li>
        <li>Last activity timestamp</li>
        <li>Tags and categories</li>
      </ul>

      <h2>Search, Filter, and Sort</h2>

      <h3>Quick Search</h3>
      <p>Use the search bar to find leads instantly:</p>
      <ul>
        <li>Search by name</li>
        <li>Search by phone number</li>
        <li>Search by email</li>
        <li>Search by company</li>
        <li>Search results update as you type</li>
      </ul>

      <h3>Advanced Filters</h3>
      <p>Click the <strong>Filter</strong> button to access advanced filtering:</p>

      <div className="not-prose space-y-3 my-4">
        <details className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Filter by Status
          </summary>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Show only leads in specific pipeline stages: New, Contacted, Qualified, etc.
          </p>
        </details>

        <details className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Filter by Source
          </summary>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Filter by lead origin: Phone, WhatsApp, Facebook, Website, Manual, Import
          </p>
        </details>

        <details className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Filter by Assignment
          </summary>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            View leads assigned to specific team members, or unassigned leads
          </p>
        </details>

        <details className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Filter by Date Range
          </summary>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Created date, last activity, or custom date fields. Use presets: Today, Yesterday, Last 7 days, This month, etc.
          </p>
        </details>

        <details className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Filter by Custom Fields
          </summary>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Filter by any custom field values: Budget range, Industry, Company size, etc.
          </p>
        </details>

        <details className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Filter by Tags
          </summary>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Find leads with specific tags or tag combinations
          </p>
        </details>
      </div>

      <h3>Sorting</h3>
      <p>Click any column header to sort:</p>
      <ul>
        <li><strong>Ascending</strong> (A-Z, oldest first, lowest first)</li>
        <li><strong>Descending</strong> (Z-A, newest first, highest first)</li>
        <li>Multi-level sorting by clicking multiple columns</li>
      </ul>

      <h3>Saving Filter Views</h3>
      <ol>
        <li>Apply your desired filters</li>
        <li>Click <strong>Save View</strong></li>
        <li>Name your view (e.g., "Hot Leads", "Uncontacted This Week")</li>
        <li>Access saved views from dropdown</li>
        <li>Share views with team members</li>
      </ol>

      <h2>Lead Actions</h2>

      <h3>Bulk Actions</h3>
      <p>Select multiple leads to perform batch operations:</p>
      <ul>
        <li><strong>Bulk Assign</strong> - Assign to team member</li>
        <li><strong>Bulk Status Change</strong> - Update pipeline stage</li>
        <li><strong>Bulk Tag</strong> - Add tags to multiple leads</li>
        <li><strong>Bulk Delete</strong> - Remove multiple leads</li>
        <li><strong>Bulk Export</strong> - Export selected leads only</li>
      </ul>

      <h3>Individual Lead Actions</h3>
      <p>Actions available in lead detail modal:</p>
      <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-3 my-4">
        <div className="flex items-center space-x-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <Phone className="w-5 h-5 text-brandColor" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">Make Call</span>
        </div>
        <div className="flex items-center space-x-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <Mail className="w-5 h-5 text-green-600" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">Send Email</span>
        </div>
        <div className="flex items-center space-x-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <MessageSquare className="w-5 h-5 text-green-600" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">Send WhatsApp</span>
        </div>
        <div className="flex items-center space-x-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <Calendar className="w-5 h-5 text-purple-600" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">Schedule Event</span>
        </div>
        <div className="flex items-center space-x-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <Edit className="w-5 h-5 text-orange-600" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">Edit Details</span>
        </div>
        <div className="flex items-center space-x-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <Trash2 className="w-5 h-5 text-red-600" />
          <span className="text-sm font-medium text-gray-900 dark:text-white">Delete Lead</span>
        </div>
      </div>

      <h2>Best Practices for Lead Management</h2>

      <div className="not-prose space-y-4 my-6">
        <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-4">
          <div className="flex items-start space-x-3">
            <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-green-900 dark:text-green-200 mb-1">
                Respond Quickly
              </h4>
              <p className="text-sm text-green-800 dark:text-green-300">
                Leads contacted within 5 minutes of capture are 9x more likely to convert.
                Enable notifications and assign leads immediately.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4">
          <div className="flex items-start space-x-3">
            <FileText className="w-5 h-5 text-brandColor dark:text-green-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-green-900 dark:text-green-200 mb-1">
                Document Everything
              </h4>
              <p className="text-sm text-green-800 dark:text-green-300">
                Add detailed remarks after every call or interaction. Future team members
                (or yourself) will thank you for the context.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900/20 border-l-4 border-purple-500 p-4">
          <div className="flex items-start space-x-3">
            <Users className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-purple-900 dark:text-purple-200 mb-1">
                Keep Leads Organized
              </h4>
              <p className="text-sm text-purple-800 dark:text-purple-300">
                Regularly update lead status. Stale leads in "New" stage for 30+ days
                indicate a process problem.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-500 p-4">
          <div className="flex items-start space-x-3">
            <Tag className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-orange-900 dark:text-orange-200 mb-1">
                Use Tags Strategically
              </h4>
              <p className="text-sm text-orange-800 dark:text-orange-300">
                Create a tagging system: Product interest, Lead quality, Geographic location,
                Industry, Urgency level, etc.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4">
          <div className="flex items-start space-x-3">
            <Calendar className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-red-900 dark:text-red-200 mb-1">
                Set Follow-Up Reminders
              </h4>
              <p className="text-sm text-red-800 dark:text-red-300">
                Always schedule the next touchpoint before ending a conversation.
                Use the Events feature to never miss a follow-up.
              </p>
            </div>
          </div>
        </div>
      </div>

      <h2>Common Workflows</h2>

      <h3>Daily Lead Review</h3>
      <ol>
        <li>Check "New Leads" tab for overnight captures</li>
        <li>Review "My Leads" for assigned leads</li>
        <li>Check calendar for today's scheduled follow-ups</li>
        <li>Update status of leads you contacted yesterday</li>
        <li>Add remarks for any progress made</li>
      </ol>

      <h3>Weekly Lead Cleanup</h3>
      <ol>
        <li>Review leads older than 7 days in "New" status</li>
        <li>Qualify or disqualify stale leads</li>
        <li>Reassign inactive team member's leads</li>
        <li>Archive or delete dead leads</li>
        <li>Update custom fields with latest information</li>
      </ol>

      <h3>End of Month Reporting</h3>
      <ol>
        <li>Export leads created this month</li>
        <li>Filter by status to see conversion rates</li>
        <li>Review won vs lost leads</li>
        <li>Analyze lead sources - which are converting best?</li>
        <li>Share insights with team in monthly meeting</li>
      </ol>

      <h2>Next Steps</h2>
      <div className="not-prose space-y-3 mt-6">
        <a
          href="/docs/pipeline"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Master the Pipeline</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Learn how to move leads through sales stages visually
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>

        <a
          href="/docs/round-robin"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Automate Lead Assignment</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Set up round-robin distribution for your team
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>

        <a
          href="/docs/drip-campaigns"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Automate Email Follow-ups</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Create drip campaigns to nurture leads automatically
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>
      </div>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mt-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-2">
              You're Now a Lead Management Pro!
            </h3>
            <p className="text-green-800 dark:text-green-300">
              You have all the tools to capture, track, and convert leads effectively.
              Start applying these practices and watch your conversion rates improve.
              Need help? Contact support at{' '}
              <a href="mailto:support@yourcrm.com" className="underline">
                support@yourcrm.com
              </a>
            </p>
          </div>
        </div>
      </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadManagementDocs;
